import {
  Box,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FiArrowDown } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "../components/Pagination";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";
import { NewsCard } from "../components/news/NewsCard";
import { NEWS_CATEGORIES } from "../data/news";
import { getNewsDetailPath, getNewsCategoryPath } from "../config/routes";
import { news } from "../services/api";
import { getNewsSeoPathSegment } from "../utils/news";

const pageSize = 8;

export const NewsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const currentPage = Math.max(1, Number(searchParams.get("page") || 1));

  const { data, isLoading } = useQuery({
    queryKey: ["public-news", "archive", currentPage],
    queryFn: () => news.getPublic({ page: currentPage, pageSize }),
  });

  const latestItems = data?.news || [];
  const totalPages = Math.max(data?.totalPage || 1, 1);

  const scrollToCategories = () => {
    const el = document.getElementById("news-categories");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToArchive = () => {
    const el = document.getElementById("news-archive");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePageChange = (page: number) => {
    const nextPage = Math.max(1, page);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextPage === 1) {
      nextSearchParams.delete("page");
    } else {
      nextSearchParams.set("page", String(nextPage));
    }

    setSearchParams(nextSearchParams);
    window.requestAnimationFrame(scrollToArchive);
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title="اطلاع‌رسانی و محتوای"
        titleHighlight="تأمین سرمایه امید"
        subtitle="اخبار، اطلاعیه‌ها، گزارش‌ها، انتشارات و تحلیل‌های بازار سرمایه را در یک ساختار منظم و دسترس‌پذیر دنبال کنید."
        primaryAction={{
          label: "مشاهده دسته‌بندی‌ها",
          icon: FiArrowDown,
          onClick: scrollToCategories,
        }}
        secondaryAction={{
          label: "تماس با ما",
          onClick: () => navigate("/contact"),
        }}
      />

      <SectionWrapper id="news-categories">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              دسته‌بندی‌های اطلاع‌رسانی
            </Heading>
            <Text color={subtitleColor} maxW="720px">
              ساختار اطلاع‌رسانی سایت در شش دسته اصلی سامان‌دهی شده تا محتوای
              خبری، رسانه‌ای و تحلیلی با مسیر روشن‌تری در دسترس باشد.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
            {NEWS_CATEGORIES.map((category) => (
              <Box
                key={category.id}
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                cursor="pointer"
                _hover={{
                  transform: "translateY(-4px)",
                  shadow: "lg",
                  borderColor: `${category.accentColor}.300`,
                }}
                transition="all 0.3s"
                onClick={() => navigate(getNewsCategoryPath(category.id))}
              >
                <VStack align="start" spacing={4}>
                  <Box
                    w="46px"
                    h="46px"
                    borderRadius="xl"
                    bg={`${category.accentColor}.100`}
                    _dark={{ bg: `${category.accentColor}.200` }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon
                      verticalAlign="middle"
                      as={category.icon as any}
                      boxSize={6}
                      color={`${category.accentColor}.500`}
                    />
                  </Box>
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    {category.title}
                  </Heading>
                  <Text color={subtitleColor} fontSize="sm" lineHeight="1.9">
                    {category.description}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <Container maxW="container.xl" py={"80px"} id="news-archive">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
            آرشیو محتواها
          </Heading>
          <Text color={subtitleColor} maxW="720px">
            اخبار و محتواهای منتشرشده در دسته‌های مختلف اطلاع‌رسانی را به صورت
            صفحه‌بندی‌شده در این بخش دنبال کنید.
          </Text>
        </VStack>

        {isLoading ? (
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
          </HStack>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
            {latestItems.map((item) => (
              <NewsCard
                key={item._id}
                item={item}
                onClick={() =>
                  navigate(
                    getNewsDetailPath(
                      item.category,
                      item.slug,
                      getNewsSeoPathSegment(item),
                    ),
                  )
                }
              />
            ))}
          </SimpleGrid>
        )}

        {!isLoading && totalPages > 1 ? (
          <Box pt={8}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        ) : null}
      </Container>

      <Footer />
    </Box>
  );
};
